// src/components/layout/GuestMarketplaceLayout.tsx
//
// Public shell for guest discovery (/browse, /browse/:shortCode,
// /browse/:shortCode/book): the site header (with Sign In / Get Started) and
// footer around the same browse / stylist / booking pages clients use.
// A signed-in client is forwarded to the equivalent /client/* page so they keep
// their dashboard navigation.
import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import MainNavigation from '../common/MainNavigation';
import SiteFooter from '../common/SiteFooter';
import { useAppSelector } from '../../hooks/useRedux';
import { marketplacePaths } from '../../utils/guestAccess';
import './GuestMarketplaceLayout.css';

export function clientEquivalentPath(pathname: string): string {
  const client = marketplacePaths(true);
  const [, , stylistRef, action] = pathname.split('/'); // ['', 'browse', ref?, 'book'?]
  if (!stylistRef) return client.browse;
  if (action === 'book') return client.booking(stylistRef);
  return client.stylist(stylistRef);
}

const GuestMarketplaceLayout: React.FC = () => {
  const location = useLocation();
  const { token, user, status } = useAppSelector((state) => state.auth);
  const userType = user?.userType || user?.role;

  // Mid-transition (logout pending, session restoring) the stale user is still in
  // the store: forwarding now would bounce a user who just logged out from
  // /browse to /client/* and then to /login. Wait until auth settles.
  const signedIn = Boolean(token && user) && status !== 'loading';
  if (signedIn && userType === 'client' && user.emailVerified) {
    return <Navigate to={clientEquivalentPath(location.pathname)} state={location.state} replace />;
  }
  // Booking outside /client is guest-only (date/time, then sign-in). Signed-in
  // non-clients (stylists, admins, unverified clients) stay on the profile, the
  // same as /client/booking's ProtectedRoute would keep them out.
  const [, , stylistRef, action] = location.pathname.split('/');
  if (signedIn && action === 'book') {
    return <Navigate to={marketplacePaths(false).stylist(stylistRef)} replace />;
  }

  return (
    <div className="guest-marketplace">
      <MainNavigation variant="solid" />
      <main className="guest-marketplace__content">
        <Outlet />
      </main>
      <SiteFooter />
    </div>
  );
};

export default GuestMarketplaceLayout;
